import { useCallback, useMemo } from "react";
import {
  AccountData,
  CustomTicketV2,
  Event,
  PromoCode,
  SavedFormQuestion,
  TicketV2,
} from "@markit/common.types";
import { Checkout } from "./Checkout";
import "../../css/FullEvent/Checkout.css";
import { m, LazyMotion, domAnimation } from "framer-motion";
import { ThemeStyle } from "../../hooks/useTheme";
import RecaptchaVerifierWrapper from "../Wrappers/RecaptchaVerifierWrapper";
import { Icon } from "@iconify/react";
import { checkIsDesktop } from "../../utils/screenResponsivenessUtils";
import { useWindowResize } from "../../hooks/useWindowResize";

type CheckoutContainerProps = {
  event: Event;
  host: AccountData;
  tixId: string;
  ticketData?: TicketV2;
  ticketToRedeem: CustomTicketV2 | undefined;
  promoterId: string;
  promoCode?: PromoCode;
  userReferrer: string;
  selectedCustomTickets: CustomTicketV2[];
  setSelectedCustomTickets: (selectedTickets: CustomTicketV2[]) => void;
  eventFormQuestions: SavedFormQuestion[];
  isEventAtCapacity: boolean;
  setShowCheckoutForm: (showCheckoutForm: boolean) => void;
  theme: ThemeStyle;
};

const CheckoutContainer = ({
  event,
  host,
  tixId, // redeem
  ticketData,
  ticketToRedeem,
  promoterId, // promoterId
  promoCode, // promoCode
  userReferrer,
  selectedCustomTickets,
  setSelectedCustomTickets,
  eventFormQuestions,
  isEventAtCapacity,
  setShowCheckoutForm,
  theme,
}: CheckoutContainerProps) => {
  const windowWidth = useWindowResize();

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth),
    [windowWidth]
  );

  const exitPressed = useCallback(async () => {
    // Reset the selectedCustomTickets to the first selected option
    setSelectedCustomTickets([selectedCustomTickets[0]]);
    setShowCheckoutForm(false);
  }, [selectedCustomTickets, setSelectedCustomTickets, setShowCheckoutForm]);

  return (
    <RecaptchaVerifierWrapper>
      <div
        className={"CheckoutFormContainer"}
        style={{ ...theme.SubComponentBG, minHeight: "100vh" }}
      >
        <div
          style={
            isDesktopSize
              ? {
                  position: "absolute",
                  zIndex: 200,
                  top: 24,
                  left: 24,
                }
              : {
                  position: "static",
                  alignSelf: "flex-start",
                  paddingTop: 14,
                  paddingLeft: 14,
                }
          }
        >
          <div
            className="Centering"
            style={{
              width: isDesktopSize ? 43 : 30,
              height: isDesktopSize ? 43 : 30,
              backgroundColor: theme.NewPrimaryBG.backgroundColor,
              borderRadius: 12,
              cursor: "pointer",
              border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
            }}
            onClick={exitPressed}
          >
            <Icon
              icon={"mdi:close"}
              height={isDesktopSize ? 24 : 18}
              style={{ color: theme.PrimaryText.color }}
            />
          </div>
        </div>
        <LazyMotion features={domAnimation}>
          <m.div
            initial={{ y: "20px", opacity: 0, filter: "blur(2px)" }}
            animate={{ y: "0px", opacity: 1, filter: "blur(0px)" }}
            exit={{ y: "20px", opacity: 0, filter: "blur(2px)" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={{
              overflowY: isDesktopSize ? "auto" : "hidden",
              maxHeight: isDesktopSize ? "100vh" : "100%",
            }}
            className="HideScrollbar"
          >
            <Checkout
              event={event}
              host={host}
              tixId={tixId}
              ticketData={ticketData}
              ticketToRedeem={ticketToRedeem}
              promoterId={promoterId}
              promoCode={promoCode}
              userReferrer={userReferrer}
              selectedCustomTickets={selectedCustomTickets}
              setSelectedCustomTickets={setSelectedCustomTickets}
              isEventAtCapacity={isEventAtCapacity}
              eventFormQuestions={eventFormQuestions}
            />
          </m.div>
        </LazyMotion>
      </div>
    </RecaptchaVerifierWrapper>
  );
};
export default CheckoutContainer;
